import { API_BASE_URL } from "../config/api";
import { crearHeadersApi, mensajeHttpSeguro } from "../servicios/httpUtils";
import {
  actualizarTokensSesion,
  limpiarSesion,
  obtenerSesion,
} from "../servicios/sesionSeguridad";
import { normalizarEmpresaId } from "../servicios/empresa";

export const urlWebApi = API_BASE_URL;

const ENDPOINTS_SIN_REFRESH = new Set([
  "piseunlogin/validaroperador",
  "piseunlogin/refresh",
  "piseunlogin/enviopassword",
  "piseunlogin/logout",
]);

let refreshEnCurso = null;

const normalizarEndpoint = (endpoint) => String(endpoint || "").replace(/^\/+/, "").toLowerCase();

const emitirSesionExpirada = (mensaje = "La sesión venció. Inicie sesión nuevamente.") => {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("piseun:sesion-invalida", { detail: mensaje }));
  }
};

const leerJson = async (response) => {
  if (response.status === 204) return {};
  try {
    return await response.json();
  } catch {
    return null;
  }
};

export const prepararParametrosApi = (parametros, sesion = obtenerSesion()) => {
  if (!parametros || typeof parametros !== "object" || Array.isArray(parametros)) return parametros;
  // Una vez autenticado, la empresa del token es la única fuente válida.
  if (sesion?.EmpresaId) {
    return Object.prototype.hasOwnProperty.call(parametros, "empresaId")
      ? { ...parametros, empresaId: sesion.EmpresaId }
      : { ...parametros, EmpresaId: sesion.EmpresaId };
  }
  if (Object.prototype.hasOwnProperty.call(parametros, "empresaId")) {
    return { ...parametros, empresaId: normalizarEmpresaId(parametros.empresaId) };
  }
  if (Object.prototype.hasOwnProperty.call(parametros, "EmpresaId")) {
    return { ...parametros, EmpresaId: normalizarEmpresaId(parametros.EmpresaId) };
  }
  return parametros;
};

const renovarSesion = async () => {
  if (refreshEnCurso) return refreshEnCurso;
  refreshEnCurso = (async () => {
    const sesion = obtenerSesion();
    if (!sesion?.RefreshToken || !sesion.EmpresaId) return false;
    try {
      const response = await fetch(`${urlWebApi}PISeunLogin/Refresh`, {
        method: "POST",
        headers: crearHeadersApi(),
        body: JSON.stringify({ EmpresaId: sesion.EmpresaId, RefreshToken: sesion.RefreshToken }),
      });
      const data = await leerJson(response);
      if (!response.ok || !data || data.Error) return false;
      actualizarTokensSesion(data);
      return true;
    } catch {
      return false;
    }
  })();

  try {
    return await refreshEnCurso;
  } finally {
    refreshEnCurso = null;
  }
};

const manejar401 = async (endpoint, reintentado) => {
  if (reintentado || ENDPOINTS_SIN_REFRESH.has(normalizarEndpoint(endpoint))) return false;
  // Un 401 recibido antes de iniciar sesión no representa una sesión vencida.
  // En particular, evita que una llamada incidental desde /login recargue /login en bucle.
  const sesionActual = obtenerSesion();
  if (!sesionActual?.IdentidadId || !sesionActual?.EmpresaId) return false;
  const renovada = await renovarSesion();
  if (!renovada) {
    limpiarSesion();
    emitirSesionExpirada();
  }
  return renovada;
};

const respuestaError = (response, data) => ({
  error: mensajeHttpSeguro(response.status, data),
  status: response.status,
});

const ejecutarPost = async (endpoint, parametros, opciones = {}, reintentado = false) => {
  const sesion = obtenerSesion();
  const response = await fetch(urlWebApi + endpoint, {
    method: "POST",
    headers: crearHeadersApi("application/json, text/plain", sesion?.AccessToken),
    body: JSON.stringify(prepararParametrosApi(parametros, sesion)),
    signal: opciones.signal,
  });
  const data = await leerJson(response);

  if (response.status === 401 && await manejar401(endpoint, reintentado)) {
    return ejecutarPost(endpoint, parametros, opciones, true);
  }
  if (!response.ok) return respuestaError(response, data);
  if (data === null) return { error: "El servicio devolvió una respuesta inválida." };
  const errorApi = data?.Error || data?.Message || data?.error;
  if (errorApi && !opciones.preservarDatosConError) return { error: errorApi };
  return { data };
};

export async function _Componentes_WebApiCalls_Post(endpoint, parametros, opciones = {}) {
  try {
    return await ejecutarPost(endpoint, parametros, opciones);
  } catch (error) {
    if (error?.name === "AbortError") return { aborted: true };
    if (/empresa valida/i.test(String(error?.message || ""))) return { error: error.message };
    return { error: "No se pudo conectar con el servicio. Verifique su conexión.", esErrorRed: true };
  }
}

export async function _Componentes_WebApiCalls_PostBlob(endpoint, parametros, opciones = {}, reintentado = false) {
  try {
    const sesion = obtenerSesion();
    const response = await fetch(urlWebApi + endpoint, {
      method: "POST",
      headers: {
        ...crearHeadersApi("application/pdf, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, text/csv, application/octet-stream", sesion?.AccessToken),
        "Content-Type": "application/json",
      },
      body: JSON.stringify(prepararParametrosApi(parametros, sesion)),
      signal: opciones.signal,
    });
    if (response.status === 401 && await manejar401(endpoint, reintentado)) {
      return _Componentes_WebApiCalls_PostBlob(endpoint, parametros, opciones, true);
    }
    if (!response.ok) {
      const data = await leerJson(response);
      return {
        error: data?.Error || data?.Message || data?.error || "No se pudo generar el archivo solicitado.",
        status: response.status,
      };
    }
    return {
      data: await response.blob(),
      contentDisposition: response.headers.get("content-disposition") || "",
    };
  } catch (error) {
    if (error?.name === "AbortError") return { aborted: true };
    if (/empresa valida/i.test(String(error?.message || ""))) return { error: error.message };
    return { error: "No se pudo conectar con el servicio. Verifique su conexión.", esErrorRed: true };
  }
}

export const __resetRefreshParaPruebas = () => { refreshEnCurso = null; };
